
/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import isEmpty from 'lodash.isempty';
import {GenericObject} from '@ion/core/src/types/entities';
import {getPlatFormIds} from '@ion/core/src/utils/DeviceFileUtils';
import {safeJSONParse} from '@ion/core/src/utils/JSONUtils';
import {SortModelType} from '@ion/dls/src/grid/IonGridUtils';

type GridHeight = number | null | undefined;

/**
 * The two directions an observed id set can move in one reconciliation - see
 * `diffSets`.
 */
export interface SetDiff {
  added: number[],
  removed: number[]
}

enum TaskType {
  FILE = 'File'
};

enum DeviceStatus {
  TOTAL = 'total',
  COMPLETED = 'completed',
  FAILED = 'failed',
  PENDING = 'pending',
  CANCELLED = 'cancelled'
}

/**
 * The `progressStatus` values which mean a job is finished, and will therefore
 * never notify again.
 *
 * These are the values the catch-up query asks the service for, so they carry
 * the service's own casing. The exact set is an open ask with the API team -
 * the client has only ever needed `Failed` - so it is deliberately the one
 * list to change when that comes back.
 */
export const TERMINAL_JOB_STATUSES = ['Completed', 'Failed', 'Cancelled'];

const terminalStatuses = TERMINAL_JOB_STATUSES
  .map(status => status.toLowerCase())
  .concat('inprogresscancelled');

/**
 * Whether a job has reached a status it can never leave, so nothing about it
 * will change again: it needs no live subscription, and it is offered no bell.
 *
 * Fails open: an unrecognised status is *not* terminal. `progressStatus` is a
 * bare string on the list response and the enums above do not agree on its full
 * range, so a predicate which guessed closed would silently unsubscribe a
 * running job - a quiet wrong answer. Guessing open costs one subscription, and
 * a bell on a job which will never ring it.
 *
 * Deliberately not `isJobCancelDisabled` or `isRerunFailedAllowed`
 * (`JobSearchEngineRowEditor`): neither is a terminal-state question. The
 * first lists `Pending`, which is the state most likely to change next; the
 * second lists only `Completed` and `Failed`.
 */
export const isTerminalJobStatus = (status?: string): boolean => (
  terminalStatuses.includes(String(status ?? '').toLowerCase())
);

/**
 * The clause which matches nothing. `(jobId:)` is invalid, so an empty watch
 * list needs an id which cannot exist - and this is the state a brand-new
 * account lands on, not an edge case.
 */
export const NO_WATCHED_JOBS_CLAUSE = '(jobId:-1)';

/**
 * Builds the search-text clause for a set of watched jobs.
 *
 *  **The ids belong in the search text, never in the `filters` array.**
 * `isMaxIds` is `jobIds?.length > 20`, and the `termsSearch` branch it selects
 * carries no `query_string` at all - so routing the ids through `filters` would
 * silently discard every facet clause above 20 watched jobs, passing every
 * manual test and failing for real users. Leaving `filters` empty is what keeps
 * that branch unreachable.
 *
 * @param   jobIds The watched job ids, in any order.
 * @returns One `query_string`-compatible OR clause, or the match-nothing
 *          sentinel when nothing is watched.
 */
export const buildWatchedJobsQuery = (jobIds: number[]): string => (
  jobIds?.length
    ? `(${jobIds.map(jobId => `jobId:${jobId}`).join(' OR ')})`
    : NO_WATCHED_JOBS_CLAUSE
);

/**
 * The clause which narrows a query to finished jobs only, composed onto the
 * watched-ids clause by the catch-up query. Costs nothing, because the ids
 * already travel in the search text.
 */
export const buildTerminalStatusQuery = (): string => (
  `(${TERMINAL_JOB_STATUSES.map(status => `progressStatus:${status}`).join(' OR ')})`
);

/**
 * The `type` every tab opened for a single job carries, assigned by the jobs
 * page when it opens one.
 */
export const JOB_TAB_TYPE = 'job-view';

/**
 * How many job tabs may be open at once.
 *
 * This is today's effective limit preserved exactly: `MAX_TABS` is 7 and there
 * were two permanent tabs, so a user has always had five job tabs. Counting
 * *all* tabs against `MAX_TABS` means every permanent tab added costs the user
 * a job tab silently, which is why the count below is of job tabs specifically.
 * Raising the limit is a separate product decision, not a side effect of adding
 * a tab.
 */
export const MAX_JOB_TABS = 5;

/**
 * How many of these tabs were opened for a single job.
 */
export const countJobTabs = (tabs: {type?: string}[]): number => (
  (tabs ?? []).filter(tab => tab?.type === JOB_TAB_TYPE).length
);

/**
 * Whether another job tab can still be opened.
 *
 * Every consumer of the limit must go through this, or the Job ID links, the
 * row double-click and the row menu's View action go dead at different points.
 */
export const isJobTabLimitReached = (tabs: {type?: string}[]): boolean => (
  countJobTabs(tabs) >= MAX_JOB_TABS
);

const fieldReplacements = {
  jobId     : 'jobId',
  createdOn : 'created_date'
};

const normalizeField = {
  created_date : 'createdOn'
};

const generateSortString = (sort: string, field: string) => {
  return {
    propertyName : field,
    order        : sort?.toUpperCase()
  };
};

export const sortModelConverter = (
  sortModel: SortModelType[],
  replacements: GenericObject = fieldReplacements
): Array<{ propertyName: string, order: 'ASC' | 'DESC' }> => {
  const results = [];
  if (sortModel?.length && !isEmpty(sortModel[0])) {
    sortModel.forEach(({colId, sort}) => {
      let field        = replacements[colId] || colId;
      field            = normalizeField[field] || field;
      const sortObject = generateSortString(sort, field);
      results.push(sortObject);
    });
  }
  return results;
};

const getTotalCount = (tasks, status = DeviceStatus.TOTAL) => {
  return tasks?.length && tasks.reduce((sum, current) => {
    return sum + Number(current?.counts?.[status] ?? 0);
  }, 0);
};

export const getTasksDevicesCounts = (tasks) => {
  return {
    total     : getTotalCount(tasks, DeviceStatus.TOTAL),
    completed : getTotalCount(tasks, DeviceStatus.COMPLETED),
    pending   : getTotalCount(tasks, DeviceStatus.PENDING),
    failed    : getTotalCount(tasks, DeviceStatus.FAILED),
    cancelled : getTotalCount(tasks, DeviceStatus.CANCELLED)
  };
};

/**
 * Determines whether a job should be disabled based on task platform
 * IDs and available file permissions.
 */
export const handleDisable = (job, features, filePermissions) => {
  const availablePlatformIds = getPlatFormIds(features, filePermissions);
  const tasksToCheck         = job?.task || job?.tasks;

  if (!tasksToCheck?.length || !filePermissions) {
    return true;
  }
  if (!availablePlatformIds || availablePlatformIds?.length === 0) {
    return true;
  }

  return !tasksToCheck?.every(task => {
    const platformId = task?.metadata?.platformId || task?.platformId;
    return platformId && availablePlatformIds?.includes(platformId);
  });
};

/**
 * Generates a list of unique platform names from provided tasks and device types.
 *
 * This function filters the list of tasks to ensure each platformId appears only once,
 * then maps those filtered tasks to their corresponding platform names defined in
 * the deviceTypes configuration.
 *
 * // Example input
 * const tasks = [
 *   { metadata: { platformId: 'ios' } },
 *   { metadata: { platformId: 'android' } },
 *   { metadata: { platformId: 'ios' } }
 * ];
 *
 * const deviceTypes = [
 *   { fileTypes: [{ platformId: 'ios', platformName: 'iOS' }, { platformId: 'android', platformName: 'Android' }] }
 * ];
 *
 * // Example output
 * const result = formatFileTypes(tasks, deviceTypes);
 * // result -> ['iOS', 'Android']
 */
export const formatFileTypes = (tasks, deviceTypes) => {
  const filteredTasks = tasks?.filter((task, index, self) => {
    const platformId = task?.metadata?.platformId;
    return self?.findIndex(item => item?.metadata?.platformId === platformId) === index;
  });

  if (deviceTypes?.length) {
    const fileTypes = deviceTypes?.flatMap(type => type?.fileTypes || []);

    return filteredTasks?.map(task => {
      const platformId   = task?.metadata?.platformId;
      const deviceTypeId = fileTypes?.find(type => type?.platformId === platformId);

      return deviceTypeId?.platformName || [];
    });
  }
};

/**
 * Returns a reliable height value by applying a priority fallback strategy:
 * 1. Use customHeight if it's a valid non‑zero value.
 * 2. Otherwise use stableHeight if available and non‑zero.
 * 3. If both are zero, null, or undefined, return the fallback (default: 94).
 *
 * Ensures that the UI never receives a height of 0.
 */
export const resolveHeight = (
  customHeight: GridHeight,
  stableHeight: GridHeight,
  fallback = 94
) => {
  if (customHeight && customHeight > 0) {
    return customHeight;
  }
  if (stableHeight && stableHeight > 0) {
    return stableHeight;
  }
  return fallback;
};

/**
 * The parseable job ids in a mixed list, as numbers.
 *
 * A tab's id is a string while the wire delivers a number, so every seam which
 * matches one against the other has to coerce first - an uncoerced id silently
 * never matches. Anything which does not parse is dropped rather than carried
 * through as `NaN`, which would compare false against everything including
 * itself.
 *
 * @param   ids The ids to coerce, in any mix of string and number.
 * @returns The ids which parsed, in the order given.
 */
export const toNumericIds = (ids?: (number | string)[]): number[] => (
  (ids ?? []).map(Number).filter(id => !isNaN(id))
);

/**
 * Whether one react-query cache key addresses the given base key *and* the
 * given job.
 *
 * `usePaginatedQueryCache` stringifies its keys (`safeJSONStringify` over
 * `sortByKey(createKey(baseKey, params))`), so they are opaque JSON strings
 * rather than arrays and react-query's own `queryKey` prefix matching cannot
 * see inside them - an invalidation has to supply a predicate instead, and a
 * predicate is handed *every* query in the cache.
 *
 * Hence the two stages. The base key is tested against the raw string first,
 * which costs nothing and rejects the whole cache of a long session; only the
 * few keys which survive are parsed, and the parse is what confirms the base
 * key really is the first element and that the job matches.
 *
 * @param   queryKey The key react-query holds, stringified or otherwise.
 * @param   baseKey The `baseKey` the query was registered under.
 * @param   jobId The job to match, compared numerically.
 * @returns `true` when the key is that base key's, for that job.
 */
export const isJobScopedKey = (
  queryKey: unknown, baseKey: string, jobId?: number | string
): boolean => {
  const key = String(queryKey);
  let mine  = false;

  if (baseKey && key.includes(baseKey)) {
    const parsed = safeJSONParse<GenericObject>(key) as GenericObject;

    mine = parsed?.[0] === baseKey && Number(parsed?.[1]?.jobId) === Number(jobId);
  }

  return mine;
};

/**
 * What changed between the ids currently observed and the ids now wanted.
 *
 * @param   desired The ids which should be observed after this round.
 * @param   observed The ids observed before it.
 * @returns `added` - wanted and not yet observed - and `removed`, observed and
 *          no longer wanted.
 */
export const diffSets = (desired: Set<number>, observed: Set<number>): SetDiff => ({
  added   : Array.from(desired).filter(id => !observed.has(id)),
  removed : Array.from(observed).filter(id => !desired.has(id))
});
